import type { AdminPost } from "@/hooks/useAdminStore";

interface EditorialPanelProps {
  posts: AdminPost[];
  onToggle: (id: string, active: boolean) => void;
}

export default function EditorialPanel({ posts, onToggle }: EditorialPanelProps) {
  const activeCount = posts.filter((p) => p.isActive).length;

  return (
    <div>
      {/* Stats */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        {[
          { label: "Total Articles", value: posts.length, icon: "ri-article-line" },
          { label: "Published", value: activeCount, icon: "ri-eye-line", color: "text-emerald-600" },
          { label: "Hidden", value: posts.length - activeCount, icon: "ri-eye-off-line", color: "text-[#aaa]" },
        ].map((stat) => (
          <div key={stat.label} className="bg-[#fafaf9] rounded-xl p-4 flex items-center gap-4">
            <div className={`w-9 h-9 flex items-center justify-center rounded-full bg-white ${stat.color ?? "text-[#555]"}`}>
              <i className={`${stat.icon} text-lg`}></i>
            </div>
            <div>
              <div className="text-2xl font-light text-[#1a1a1a]">{stat.value}</div>
              <div className="text-xs text-[#aaa]">{stat.label}</div>
            </div>
          </div>
        ))}
      </div>

      <div className="mb-4">
        <p className="text-sm text-[#aaa]">
          Toggle articles to show or hide them in the Glow Edit section. Unpublished articles won&apos;t appear in the store.
        </p>
      </div>

      {/* Articles list */}
      <div className="space-y-3">
        {posts.map((post) => (
          <div
            key={post.id}
            className={`bg-white border border-[#f0f0f0] rounded-xl overflow-hidden flex transition-opacity ${
              !post.isActive ? "opacity-50" : ""
            }`}
          >
            {/* Cover image */}
            <div className="w-28 h-20 flex-shrink-0 overflow-hidden bg-[#f5f5f5]">
              <img
                src={post.coverImage}
                alt={post.title}
                className="w-full h-full object-cover object-top"
              />
            </div>

            {/* Content */}
            <div className="flex-1 px-5 py-3 flex items-center justify-between gap-4">
              <div className="min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs bg-[#f0f0f0] text-[#888] rounded-full px-2.5 py-0.5">
                    {post.tag}
                  </span>
                  <span className="text-xs text-[#bbb]">{post.date}</span>
                  <span className="text-xs text-[#bbb]">· {post.readTime}</span>
                </div>
                <h3 className="font-['Playfair_Display'] text-[#1a1a1a] text-sm font-medium leading-snug mb-1 truncate">
                  {post.title}
                </h3>
                <p className="text-xs text-[#aaa] truncate max-w-md">{post.excerpt}</p>
              </div>

              <div className="flex items-center gap-3 flex-shrink-0">
                <span className={`text-xs ${post.isActive ? "text-emerald-600" : "text-[#bbb]"}`}>
                  {post.isActive ? "Published" : "Hidden"}
                </span>
                <button
                  onClick={() => onToggle(post.id, !post.isActive)}
                  className={`w-10 h-5 rounded-full transition-colors relative cursor-pointer flex-shrink-0 ${
                    post.isActive ? "bg-[#1a1a1a]" : "bg-[#ddd]"
                  }`}
                >
                  <div
                    className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full transition-transform ${
                      post.isActive ? "translate-x-5" : ""
                    }`}
                  />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
